import { useEffect } from 'react';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useAppParams from 'src/hooks/useAppParams';
import useAlertNotify from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotify';
import useModal from 'src/components/Modals/hooks/useModal';

export default function useAppParamsAlertNotificationListener() {
	const appParams = useAppParams();
	const alertNotify = useAlertNotify();

	// For collateral alert.
	const spotMarkets = useSpotMarkets();
	const { show } = useModal();

	const appParamsAlert = appParams?.alert;

	const isAlertExpired =
		appParamsAlert &&
		appParamsAlert.alertData.untilMs &&
		appParamsAlert.alertData.untilMs < Date.now();

	useEffect(() => {
		if (!appParamsAlert?.alertType || isAlertExpired) {
			return;
		}

		switch (appParamsAlert.alertType) {
			case 'feature': {
				if (
					!appParamsAlert.alertData.heading ||
					!appParamsAlert.alertData.description
				) {
					return;
				}

				const { heading, description, linkText, linkUrl, imageSrc } =
					appParamsAlert.alertData;

				alertNotify({
					id: `feature-alert-${heading}`,
					// TODO: We don't have image uploads implemented in the BE so keeping
					// this `minor` for now in order to mitigate any mistakes. Need to update
					// to `major` once BE is complete.
					alertType: 'minor',
					imageSrc,
					message: heading,
					description,
					action: {
						type: 'link',
						label: linkText,
						href: linkUrl ?? '',
					},
				});

				return;
			}
			case 'warning': {
				if (
					!appParamsAlert.alertData.heading ||
					!appParamsAlert.alertData.description
				) {
					return;
				}

				const { heading, description, linkText, linkUrl } =
					appParamsAlert.alertData;

				alertNotify({
					id: `warning-alert-${heading}`,
					alertType: 'minor',
					message: heading,
					description,
					action: {
						type: 'link',
						label: linkText,
						href: linkUrl ?? '',
					},
				});

				return;
			}
			case 'market': {
				if (!appParamsAlert.alertData.baseSymbol) {
					return;
				}

				const { baseSymbol } = appParamsAlert.alertData;

				alertNotify({
					id: `market-alert-${baseSymbol}`,
					alertType: 'minor',
					message: `${baseSymbol} has landed!`,
					description: `Start trading ${baseSymbol}-PERP today!`,
					action: {
						type: 'link',
						label: 'Trade now',
						href: `/${baseSymbol}-PERP`,
					},
				});

				return;
			}
			case 'collateral': {
				if (!appParamsAlert.alertData.baseSymbol) {
					return;
				}

				const { baseSymbol } = appParamsAlert.alertData;
				const spotMarket = spotMarkets.find(
					(spotMarket) => spotMarket.symbol === baseSymbol
				);

				alertNotify({
					id: `collateral-alert-${baseSymbol}`,
					alertType: 'minor',
					message: `${baseSymbol} is now live!`,
					description: `You can now deposit ${baseSymbol}.`,
					action: {
						label: `Deposit`,
						callback: () => {
							show({
								type: 'deposit',
								props: {
									collateralIndex: spotMarket?.marketIndex ?? 0,
								},
							});
						},
					},
				});

				return;
			}
			default:
				return;
		}
	}, [appParamsAlert, isAlertExpired, show, spotMarkets, alertNotify]);
}
